import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BucketDots from './BucketDots';
import DeletedBadge from './DeletedBadge';
import EventUserList from './EventUserList';
import RefreshButton from './RefreshButton';
// Hidden now that Tag It and Rate It are separate Insights modules; kept for switch-back.
// import TiriModuleBadges from './TiriModuleBadges';

/**
 * The person band's left gutter: the back chevron (16px) plus the 8px gap to the avatar.
 *
 * A constant rather than a measured width, because the timeline below indents by the SAME number
 * to line its rows up under the avatar, and the two live in different containers — the band sits
 * outside the scroll area so it stays put while the timeline scrolls. There is nothing to
 * measure against from there.
 */
const PERSON_GUTTER_PX = 28;

const BUCKET_COLORS = {
  reach: '#0EA5E9',
  activity: '#F59E0B',
  creation: '#22C55E',
  participation: '#8B5CF6'
};

/**
 * The label lookups are interpolated i18next keys rather than JS maps.
 *
 * `eventType` is already a dotted path (`tiri.tagit.submit`) and i18next's key separator is '.',
 * so the locale file's nesting IS the event taxonomy and there is no second table here to drift
 * out of step with it. Teaching the drawer a new event is a locale edit and nothing else.
 *
 * `defaultValue: ''` is what makes the fallback chain work: a key with no entry resolves to an
 * empty string, which is falsy, so the next link is tried. Without it i18next echoes the key it
 * could not find, and an unrecognised event would print the literal string
 * "admin.insights.drawer.events.some.new.thing" onto an admin's screen.
 *
 * Interpolating a raw DB value into a key is also what keeps this safe from a plain-object
 * lookup: `EVENT_LABELS['constructor']` would have handed back a Function, whereas a missing
 * i18next key is simply a miss.
 */
const eventLabelKey = (eventType) => `admin.insights.drawer.events.${eventType}`;
const bucketFallbackKey = (bucket) => `admin.insights.drawer.bucketFallback.${bucket}`;

// Emotion reactions (fancam.react, fanchat.react) — same vocabulary as the app's reaction bar.
const REACTION_EMOJI = {
  like: '👍',
  unlike: '👎',
  love: '❤️',
  angry: '😠',
  care: '🥰',
  haha: '😂',
  sad: '😢',
  wow: '😮'
};

const PREDICTION_TYPE_LABEL = {
  half_time: 'Half-Time',
  full_time: 'Full-Time'
};

// Tag It ships one hint per item by two routes — tapping the photo, or earning it with a wrong
// guess — and `metadata.trigger` is the only thing that tells them apart, so the drawer says which.
/**
 * Same interpolated-key idiom as `eventLabelKey` above, for the two chips that qualify a row.
 *
 * `hintTrigger` is `metadata.trigger` verbatim: one Tag It hint reaches a fan two ways and only
 * this field separates them — `wrongGuess` is written by `api/tiri/tagit/submit.ts`, `tap` by
 * the app when the fan taps the photo.
 *
 * `rateItRating` deliberately points at the FAN-FACING `tiri.rateIt.ratings.*` keys rather than
 * a copy under `admin.*`. These are the backend's `RateItRating` enum values, and the tier an
 * admin reads in this timeline has to be the one the fan was actually shown; a second set would
 * be free to drift from the first without anything failing.
 */
const hintTriggerKey = (trigger) => `admin.insights.drawer.hintTrigger.${trigger}`;
const rateItRatingKey = (rating) => `tiri.rateIt.ratings.${rating}`;

const COLOR_SWATCH = {
  blue: '#3B82F6',
  red: '#EF4444',
  green: '#22C55E',
  yellow: '#EAB308',
  white: '#E5E7EB',
  black: '#111827'
};

const fmtExact = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });

const fmtRelative = (iso) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return fmtExact(iso);
};

const UserAvatar = ({ user }) =>
  user?.avatar ? (
    <img
      src={user.avatar}
      alt=""
      className="h-8 w-8 shrink-0 rounded-full object-cover bg-[#F1F5F9]"
    />
  ) : (
    <div className="h-8 w-8 shrink-0 rounded-full bg-[#EEF2FF] flex items-center justify-center text-[#3B5BDB] text-[12px] font-semibold">
      {(user?.name || user?.username || '?').charAt(0).toUpperCase()}
    </div>
  );

/**
 * True when one view type (e.g. "viewed this post") is on two or more rows. Views are the only
 * events rolled up, by the hour, so this is the case where a fan's views look duplicated.
 */
const hasSplitViewRows = (events) => {
  const rowsPerType = {};
  return events.some((event) => {
    if (event.bucket !== 'reach') return false;
    rowsPerType[event.eventType] = (rowsPerType[event.eventType] || 0) + 1;
    return rowsPerType[event.eventType] > 1;
  });
};

const EventRow = ({ event, hideUser, module }) => {
  const { t } = useTranslation();
  // Game Stat has one event today, but older rows can carry a since-renamed eventType with no
  // entry in EVENT_LABELS — falling to the generic "viewed this" reads as a different feature.
  // Module comes before the shared bucket fallback so these still read as Game Stat.
  const label =
    t(eventLabelKey(event.eventType), { defaultValue: '' }) ||
    (module === 'playerStats' && event.bucket === 'reach'
      ? t('admin.insights.drawer.playerStatsReachFallback')
      : '') ||
    t(bucketFallbackKey(event.bucket), { defaultValue: '' }) ||
    t('admin.insights.drawer.genericAction');
  const color = BUCKET_COLORS[event.bucket] || '#94A3B8';
  const reactionEmoji = REACTION_EMOJI[event.metadata?.reaction];
  const colorSwatch = COLOR_SWATCH[event.metadata?.color];
  const effectName = event.metadata?.effectName;
  const predictionType = PREDICTION_TYPE_LABEL[event.metadata?.predictionType];
  const predictedScore = event.metadata?.predictedScore;
  const tapsDelta = event.metadata?.tapsDelta;
  // A goalrush.kick row is the fan's kicks and goals summed over the session (events.ts).
  const goalRushBatch = event.eventType === 'goalrush.kick' ? event.metadata : null;
  // `correct` is read with an explicit undefined check, not for truthiness — `false` is the
  // meaningful half of this signal and a truthiness test would silently hide every wrong tag.
  const tagCorrect = event.metadata?.correct;
  // Empty string, not undefined, when the value is unrecognised — both chips are rendered
  // behind a truthiness guard, so a miss simply drops the chip.
  const rateItRating = t(rateItRatingKey(event.metadata?.rating), { defaultValue: '' });
  const hintTrigger = t(hintTriggerKey(event.metadata?.trigger), { defaultValue: '' });
  // Only rolled-up Reach rows are decorated with how many events are behind them; every other
  // bucket is a single event and must not get a meaningless "×1". A GoalRush session row is
  // rolled up too, but its chip already says what it sums, so its row count is not shown.
  const repeats = event.count > 1 && !goalRushBatch ? event.count : 0;
  // Every reach label reads as a full sentence ("viewed this post", "opened the wave") — an
  // inline "×8" after one reads oddly, so rolled-up reach gets its own "Reach 8" line below
  // instead of the shared ×N suffix, for every module.
  const useReachLine = repeats > 0;

  return (
    <div className="flex items-start gap-3 py-3">
      {!hideUser && <UserAvatar user={event.user} />}
      <div className="min-w-0 flex-1">
        <p className="text-[13px] text-[#0F172A]">
          {!hideUser && (
            <>
              <span className="font-medium">
                {event.user?.name || event.user?.username || 'A fan'}
              </span>{' '}
            </>
          )}
          <span className="text-[#334155]">{label}</span>
          {repeats > 0 && !useReachLine && (
            <span className="ml-1 font-medium text-[#64748B] tabular-nums">
              {t('admin.insights.drawer.repeatCount', { count: repeats })}
            </span>
          )}
          {reactionEmoji && <span className="ml-1">{reactionEmoji}</span>}
          {colorSwatch && (
            <span
              className="inline-block h-2.5 w-2.5 rounded-full border border-black/10 ml-1.5 align-middle"
              style={{ backgroundColor: colorSwatch }}
              title={event.metadata.color}
            />
          )}
          {effectName && <span className="ml-1 text-[#3B5BDB] font-medium">"{effectName}"</span>}
          {predictionType && <span className="ml-1 text-[#64748B]">({predictionType})</span>}
          {predictedScore && (
            <span className="ml-1 text-[#3B5BDB] font-medium">
              {predictedScore.home}–{predictedScore.away}
            </span>
          )}
          {tagCorrect !== undefined && (
            <span
              className="ml-1 font-medium"
              style={{ color: tagCorrect ? '#22C55E' : '#EF4444' }}
            >
              {tagCorrect
                ? t('admin.insights.drawer.tagCorrect')
                : t('admin.insights.drawer.tagWrong')}
            </span>
          )}
          {rateItRating && <span className="ml-1 text-[#3B5BDB] font-medium">{rateItRating}</span>}
          {hintTrigger && <span className="ml-1 text-[#64748B]">({hintTrigger})</span>}
          {tapsDelta > 0 && (
            <span className="ml-1 text-[#3B5BDB] font-medium">
              {tapsDelta} tap{tapsDelta === 1 ? '' : 's'}
            </span>
          )}
          {goalRushBatch && (
            <span className="ml-1 text-[#3B5BDB] font-medium">
              {t('admin.insights.drawer.goalrushChip', {
                kicks: goalRushBatch.kicks || 0,
                goals: goalRushBatch.goals || 0
              })}
            </span>
          )}
        </p>
        {useReachLine && (
          <p className="text-[12px] font-medium text-[#64748B] mt-0.5">
            {t('admin.insights.drawer.reachCount', { count: repeats })}
          </p>
        )}
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
          <span title={fmtExact(event.createdAt)} className="text-[11px] text-[#94A3B8]">
            {fmtRelative(event.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Right-side slide-over — the last drill-down level: one specific item's raw event timeline,
 * "who did what, when." Same overlay+panel pattern as most admin detail views (Stripe/Linear
 * style), so opening it doesn't restructure the page underneath.
 */
const ItemActivityDrawer = ({ item, module, fetchEvents, fetchEventUsers, onClose }) => {
  const { t } = useTranslation();
  /**
   * `null` means the People list is showing; a row means that fan's own timeline is.
   *
   * The drawer is two screens, not two panes: at 448px there is no room to show a list of people
   * beside a list of events, and the screen above already established the master/detail move
   * (module → items). This continues it one level down.
   */
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState(null);
  const [userPage, setUserPage] = useState(0);
  const [events, setEvents] = useState(null);
  /**
   * Two counts, deliberately not one. The item header shows the participant count at BOTH levels
   * — it is a property of the item, not of the level you are on — while the timeline's pager
   * counts one fan's events. A single `total` made the header report "People · 18" as soon as a
   * fan with 18 events was opened.
   */
  const [usersTotal, setUsersTotal] = useState(0);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [reloadNonce, setReloadNonce] = useState(0);
  const pageSize = 50;
  const userPageSize = 25;

  const viewingPerson = !!selectedUser;
  const personId = selectedUser?.user?._id;

  /**
   * Whether the NEXT effect run is a manual refresh rather than a real change of query.
   *
   * A ref and not state: it has to be readable inside the effect that the same click schedules,
   * and it must not itself be a dependency — making it one would re-run the fetch when the flag
   * resets and turn every refresh into two.
   */
  const quietRef = useRef(false);

  /**
   * One effect for both levels, keyed on `personId`, so opening a fan and going back are just
   * another query change — the same skeleton, cancellation and refresh handling covers all of it
   * rather than a second copy of this for the people list.
   */
  useEffect(() => {
    let cancelled = false;
    const quiet = quietRef.current;
    quietRef.current = false;
    // A refresh keeps what is on screen and only spins the button; a genuine change of item,
    // page, person or filter swaps to the skeleton, because the list really is about to be
    // different content.
    if (quiet) setRefreshing(true);
    else setLoading(true);

    const request = personId
      ? Promise.all([
          fetchEvents(module, item.itemId, page * pageSize, pageSize, personId).then((res) => {
            if (cancelled) return;
            setEvents(res?.events || []);
            setEventsTotal(res?.total ?? 0);
          }),
          // The selected-person header shows THIS fan's own bucket totals, snapshotted from the
          // row that was tapped — without this, a refresh updates the timeline below but leaves
          // that header stuck on stale counts.
          fetchEventUsers(module, item.itemId, 0, 1, personId).then((res) => {
            if (cancelled) return;
            const fresh = res?.users?.[0];
            if (fresh) setSelectedUser((prev) => (prev ? { ...prev, ...fresh } : prev));
          })
        ])
      : fetchEventUsers(module, item.itemId, userPage * userPageSize, userPageSize).then((res) => {
          if (cancelled) return;
          setUsers(res?.users || []);
          setUsersTotal(res?.total ?? 0);
        });

    request.then(() => {
      if (cancelled) return;
      if (quiet) setRefreshing(false);
      else setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [module, item.itemId, personId, page, userPage, fetchEvents, fetchEventUsers, reloadNonce]);

  // Refetches the SAME page of the same item. The nonce is what lets an unchanged query re-run:
  // every other dependency is by definition identical on a refresh, so without it the effect
  // would not fire at all.
  const handleRefresh = useCallback(() => {
    quietRef.current = true;
    setReloadNonce((n) => n + 1);
  }, []);

  const handleOpenUser = useCallback((row) => {
    setSelectedUser(row);
    // Their timeline starts at its own first page; carrying the people list's page number over
    // would open a fan on page 3 of events they may not have.
    setPage(0);
    setEvents(null);
  }, []);

  const handleBackToPeople = useCallback(() => {
    setSelectedUser(null);
    setEvents(null);
  }, []);

  const hasPrev = page > 0;
  const hasNext = (page + 1) * pageSize < eventsTotal;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      <div className="relative w-full max-w-md bg-white h-full shadow-xl flex flex-col">
        <div className="flex items-start gap-3 px-5 py-4 border-b border-[#E2E8F0]">
          {item.thumbnail ? (
            <img
              src={item.thumbnail}
              alt=""
              className={`h-12 w-12 shrink-0 rounded-lg object-cover bg-[#F1F5F9] ${item.deleted ? 'opacity-40 grayscale' : ''}`}
            />
          ) : (
            <div
              className={`h-12 w-12 shrink-0 rounded-lg bg-[#EEF2FF] flex items-center justify-center text-[#3B5BDB] text-[16px] font-semibold ${item.deleted ? 'opacity-40' : ''}`}
            >
              {(item.title || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            {/* PERMANENT at both levels. Opening a fan used to replace this, which took the item
                out of its own drawer — you could be three levels deep reading a timeline with
                nothing on screen naming what it was a timeline OF. The fan goes in its own band
                below instead. */}
            <p className="text-[14px] font-semibold text-[#0F172A] truncate">{item.title}</p>
            <div className="flex items-center gap-1 mt-1">
              {/* <TiriModuleBadges modules={item.modules} /> */}
              <DeletedBadge deleted={item.deleted} deletedAt={item.deletedAt} showDate />
            </div>
            {item.subtitle && (
              <p className="text-[12px] text-[#94A3B8] truncate">{item.subtitle}</p>
            )}
            <p className="text-[12px] text-[#94A3B8]">
              {t('admin.insights.drawer.people')}
              {usersTotal > 0 && ` · ${usersTotal}`}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <RefreshButton
              onClick={handleRefresh}
              refreshing={refreshing}
              disabled={loading}
              title="Refresh this item's activity"
            />
            <button
              onClick={onClose}
              className="text-[12px] font-medium text-[#64748B] hover:text-[#334155] transition-colors"
            >
              {t('admin.insights.drawer.close')}
            </button>
          </div>
        </div>

        {/* The fan being read, as a band UNDER the item rather than in place of it. Sits outside
            the scroll area so it stays put while their timeline scrolls — it is the answer to
            "whose events are these", which is exactly the question a scrolled-away header stops
            being able to answer. */}
        {viewingPerson && (
          <div className="px-5 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
            <div className="flex items-start">
              {/* Chevron only, so `backToPeople` becomes the accessible name instead of visible
                  text.
                  `items-start` lifts it to the top of the band, level with the fan's name rather
                  than the middle of the avatar. The nudge is on the GLYPH, not the button:
                  relative offsets do not affect flow, so the box keeps its
                  `PERSON_GUTTER_PX` footprint and the timeline below stays aligned to the
                  avatar. A negative margin here would have dragged both left instead. */}
              <button
                onClick={handleBackToPeople}
                title={t('admin.insights.drawer.backToPeople')}
                aria-label={t('admin.insights.drawer.backToPeople')}
                className="h-9 w-5 mr-2 shrink-0 flex items-start justify-center text-[33px] leading-none text-[#94A3B8] hover:text-[#334155] transition-colors"
              >
                <span aria-hidden="true" className="relative top-[-5px] left-[-3px]">
                  ‹
                </span>
              </button>

              <UserAvatar user={selectedUser.user} />
              <div className="min-w-0 flex-1 ml-3">
                <p className="text-[13px] font-semibold text-[#0F172A] truncate">
                  {selectedUser.user?.name ||
                    selectedUser.user?.username ||
                    t('admin.insights.drawer.deletedUser')}
                </p>
                {/* Their own bucket split, carried straight from the row that was tapped — the
                    same dots the people list showed, so the shape of their engagement stays
                    readable while the timeline underneath is being scanned. */}
                <BucketDots row={selectedUser} />
                <p className="text-[11px] text-[#94A3B8] mt-0.5">
                  {t('admin.insights.drawer.personEvents', { count: selectedUser.total })}
                  {selectedUser.lastAt &&
                    ` · ${t('admin.insights.drawer.lastActive', {
                      time: fmtRelative(selectedUser.lastAt)
                    })}`}
                </p>
              </div>
            </div>
          </div>
        )}

        <div
          className="flex-1 overflow-y-auto px-5"
          // `px-5` is 20px; the person view adds the gutter on top of it so a row's left edge
          // lands exactly on the avatar's.
          style={viewingPerson ? { paddingLeft: 20 + PERSON_GUTTER_PX } : undefined}
        >
          {!viewingPerson ? (
            <EventUserList
              users={users}
              total={usersTotal}
              loading={loading}
              page={userPage}
              pageSize={userPageSize}
              onPageChange={setUserPage}
              onUserClick={handleOpenUser}
              fmtRelative={fmtRelative}
            />
          ) : loading ? (
            <div className="py-4 space-y-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="h-8 w-8 rounded-full bg-[#F1F5F9]" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-2/3 bg-[#F1F5F9] rounded" />
                    <div className="h-2.5 w-1/4 bg-[#F1F5F9] rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : !events?.length ? (
            <div className="py-10 text-center">
              <p className="text-[13px] font-medium text-[#334155]">No activity recorded</p>
              <p className="text-[12px] text-[#94A3B8] mt-1">
                Nothing logged for this item in the current filter.
              </p>
            </div>
          ) : (
            <>
              {/* Only when the same view line appears on more than one row — the split this
                  explains. A single view row, grouped or not, needs no note. */}
              {hasSplitViewRows(events) && (
                <p className="text-[11px] text-[#94A3B8] pb-2">
                  {t('admin.insights.drawer.groupedByHour')}
                </p>
              )}
              <div className="divide-y divide-[#F1F5F9]">
                {events.map((event) => (
                  <EventRow key={event._id} event={event} hideUser module={module} />
                ))}
              </div>
            </>
          )}
        </div>

        {viewingPerson && eventsTotal > pageSize && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC]">
            <p className="text-[12px] text-[#94A3B8]">
              {page * pageSize + 1}–{Math.min((page + 1) * pageSize, eventsTotal)} of {eventsTotal}
            </p>
            <div className="flex gap-2">
              <button
                disabled={!hasPrev}
                onClick={() => setPage((p) => p - 1)}
                className="h-8 px-3 rounded-lg text-[12px] font-medium border border-[#CBD5E1] bg-white text-[#334155] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <button
                disabled={!hasNext}
                onClick={() => setPage((p) => p + 1)}
                className="h-8 px-3 rounded-lg text-[12px] font-medium border border-[#CBD5E1] bg-white text-[#334155] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemActivityDrawer;
